import { useEffect, useState } from 'react'

/**
 * Whether a CSS media query currently matches, kept in sync with the viewport.
 * `false` where `matchMedia` is unavailable (jsdom, SSR) — callers treat the
 * query as "not matching", which for a `min-width` query is the narrow case.
 *
 * FF-AUDIT-20260914-210459-087: the app shell needs the same breakpoint the
 * stylesheet uses (`lg`, 1024 px) to decide whether the sidebar may expand
 * inline; a stored "expanded" preference must not cost a 768-px tablet
 * 240 px of its 768.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(query).matches : false,
  )
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const media = window.matchMedia(query)
    const update = () => setMatches(media.matches)
    update()
    media.addEventListener('change', update)
    // Belt and braces: some embedded/emulated viewports resize without firing
    // the media query's change event; a window resize re-reads the match.
    window.addEventListener('resize', update)
    return () => {
      media.removeEventListener('change', update)
      window.removeEventListener('resize', update)
    }
  }, [query])
  return matches
}
